import React, { useMemo } from "react";

/**
 * Star-Map: radial topology graph.
 * Center = Vessel. Inner ring = Wayfinders. Outer ring = Hulls (per wayfinder cluster).
 */
const STAR_W = 900;
const STAR_H = 520;
const HULL_COLOR = {
  url: "#dcb95f",
  "shell-sentry": "#6cc88f",
  cloud: "#5b88c2",
};

const polar = (cx, cy, r, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const StarMap = ({ data }) => {
  const { nodes, edges, stats } = data;

  const layout = useMemo(() => {
    const cx = STAR_W / 2;
    const cy = STAR_H / 2;
    const wayfinders = nodes.filter((n) => n.type === "wayfinder");
    const hulls = nodes.filter((n) => n.type === "hull");
    const positions = { vessel: { x: cx, y: cy } };

    const wfRadius = 130;
    wayfinders.forEach((w, i) => {
      const angle = (360 / Math.max(1, wayfinders.length)) * i - 90;
      positions[w.id] = { ...polar(cx, cy, wfRadius, angle), angle };
    });

    // group hulls by wayfinder
    const grouped = {};
    edges.forEach((e) => {
      if (e.kind !== "command") {
        grouped[e.from] = grouped[e.from] || [];
        grouped[e.from].push(e.to);
      }
    });

    Object.keys(grouped).forEach((wid) => {
      const wf = positions[wid];
      if (!wf) return;
      const list = grouped[wid];
      const arc = Math.min(140, Math.max(40, list.length * 18));
      const start = wf.angle - arc / 2;
      list.forEach((hid, idx) => {
        const a = start + (arc / Math.max(1, list.length - 1 || 1)) * idx;
        positions[hid] = polar(cx, cy, 230, a);
      });
    });

    return positions;
  }, [nodes, edges]);

  return (
    <svg viewBox={`0 0 ${STAR_W} ${STAR_H}`} className="w-full h-auto" data-testid="starmap-svg">
      <defs>
        <radialGradient id="vessel-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#dcb95f" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#dcb95f" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#dcb95f" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* concentric rings */}
      {[80, 130, 180, 230].map((r) => (
        <circle
          key={r}
          cx={STAR_W / 2} cy={STAR_H / 2} r={r}
          fill="none" stroke="rgba(200,162,75,0.07)" strokeWidth="0.5"
          strokeDasharray={r === 130 || r === 230 ? "4 4" : "none"}
        />
      ))}

      {/* vessel halo */}
      <circle cx={STAR_W / 2} cy={STAR_H / 2} r={70} fill="url(#vessel-glow)" />

      {/* edges */}
      {edges.map((e, i) => {
        const a = layout[e.from], b = layout[e.to];
        if (!a || !b) return null;
        const isCommand = e.kind === "command";
        return (
          <line
            key={i}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke={isCommand ? "rgba(220,185,95,0.45)" : (HULL_COLOR[e.kind] || "#dcb95f") + "66"}
            strokeWidth={isCommand ? 1.2 : 0.7}
            strokeDasharray={isCommand ? "" : "2 3"}
          />
        );
      })}

      {/* vessel center */}
      <g>
        <rect
          x={STAR_W / 2 - 12} y={STAR_H / 2 - 12} width={24} height={24}
          transform={`rotate(45 ${STAR_W / 2} ${STAR_H / 2})`}
          fill="rgba(220,185,95,0.15)" stroke="#dcb95f" strokeWidth="1.2"
        />
        <text x={STAR_W / 2} y={STAR_H / 2 + 4} textAnchor="middle"
              fill="#dcb95f" fontSize="9" letterSpacing="0.18em"
              fontFamily="JetBrains Mono">EIGOO</text>
      </g>

      {/* wayfinder nodes */}
      {nodes.filter((n) => n.type === "wayfinder").map((w) => {
        const p = layout[w.id]; if (!p) return null;
        return (
          <g key={w.id} data-testid={`starmap-wf-${w.id}`}>
            <circle cx={p.x} cy={p.y} r={11}
                    fill="rgba(220,185,95,0.18)" stroke="#dcb95f" strokeWidth="1.2" />
            <text x={p.x} y={p.y + 3} textAnchor="middle"
                  fill="#0c0c11" fontSize="8" fontFamily="JetBrains Mono"
                  fontWeight="700">{w.hull_count}</text>
            <text x={p.x} y={p.y - 18} textAnchor="middle"
                  fill="#ece4cf" fontSize="9" letterSpacing="0.14em"
                  fontFamily="JetBrains Mono">{w.label}</text>
          </g>
        );
      })}

      {/* hull nodes */}
      {nodes.filter((n) => n.type === "hull").map((h) => {
        const p = layout[h.id]; if (!p) return null;
        const color = HULL_COLOR[h.hull_type] || "#dcb95f";
        return (
          <g key={h.id} data-testid={`starmap-hull-${h.id.slice(0,6)}`}>
            <circle cx={p.x} cy={p.y} r={5} fill={color} stroke="#0c0c11" strokeWidth="0.8">
              <title>{h.hull_type} · {h.label}</title>
            </circle>
            <circle cx={p.x} cy={p.y} r={9} fill={color} opacity="0.15">
              <animate attributeName="r" values="6;14;6" dur="3.2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.18;0;0.18" dur="3.2s" repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}

      {/* legend */}
      <g transform={`translate(20,${STAR_H - 60})`}>
        <text fill="#cfc6ad" fontFamily="JetBrains Mono" fontSize="9" letterSpacing="0.2em">LEGEND</text>
        <g transform="translate(0,14)">
          <circle cx="6" cy="6" r="4" fill={HULL_COLOR.url} />
          <text x="16" y="9" fill="#ece4cf" fontFamily="JetBrains Mono" fontSize="9">URL Scan</text>
        </g>
        <g transform="translate(0,30)">
          <circle cx="6" cy="6" r="4" fill={HULL_COLOR["shell-sentry"]} />
          <text x="16" y="9" fill="#ece4cf" fontFamily="JetBrains Mono" fontSize="9">Shell-Sentry</text>
        </g>
        <g transform="translate(0,46)">
          <circle cx="6" cy="6" r="4" fill={HULL_COLOR.cloud} />
          <text x="16" y="9" fill="#ece4cf" fontFamily="JetBrains Mono" fontSize="9">Cloud Bridge</text>
        </g>
      </g>

      <g transform={`translate(${STAR_W - 180},${STAR_H - 60})`}>
        <text fill="#cfc6ad" fontFamily="JetBrains Mono" fontSize="9" letterSpacing="0.2em">TOPOLOGY</text>
        <text x="0" y="20" fill="#dcb95f" fontFamily="Cinzel" fontSize="13">{stats.wayfinders} wayfinders</text>
        <text x="0" y="38" fill="#dcb95f" fontFamily="Cinzel" fontSize="13">{stats.hulls} hulls</text>
      </g>
    </svg>
  );
};

export default StarMap;
